import PropTypes from 'prop-types'
import React from 'react'
import { reduxForm, SubmissionError, isPristine } from 'redux-form'
import { getFormattedPhoneNumber } from 'SRC/utils'
import { connect } from 'react-redux'
import { compose } from 'recompose'
import withTranslation from 'next-translate/withTranslation'
import { getUserInfo, getNewLogo, getNewBanner, getServiceProviderPhotos } from '../../../selectors'
import {
  Title,
  Description,
  Addresses,
  AdditionalInfo,
  ChangePassword,
  Contacts,
  Logo,
  Banner,
  Photos
} from '../Elements'
import { FormNotification } from '../FormNotification'
import { emailValidator, passwordValidator, phoneValidator, requiredValidator } from 'Core/validators'
import {
  setProfileUpdateError,
  setUserInfo,
  setProfileLoading,
  setNotifications
} from '../../../actions'
import { dataURLtoFile } from 'SRC/utils/Utils'
import { ProfileApi } from '../../../api'
import { CookiesHelper } from 'Core/cookiesHelper/CookiesHelper'
import { setIsAuthorized } from '../../../../Auth/actions'
import config from 'SRC/config/config.json'

const isBusinessUser = type => type && type !== config.userTypes.freeUserTypeSeo

const isServiceProvider = type => type === config.userTypes.serviceProviderSeo

const getLogoDiff = (initialLogo, currentLogo) => {
  const diff = {}
  if (currentLogo) {
    if (!currentLogo.isOriginal && currentLogo.file && currentLogo.name) {
      diff.logo = dataURLtoFile(currentLogo.file, currentLogo.name)
    }
  } else {
    if (initialLogo) {
      diff.logo = null
    }
  }
  return Object.keys(diff).length ? diff : null
}

const getBannerDiff = (initialBanner, currentBanner) => {
  const diff = {}
  if (currentBanner) {
    if (!currentBanner.isOriginal && currentBanner.file && currentBanner.name) {
      diff.banner = dataURLtoFile(currentBanner.file, currentBanner.name)
    }
  } else {
    if (initialBanner) {
      diff.banner = null
    }
  }
  return Object.keys(diff).length ? diff : null
}

const getPhotosWithNewMeta = (currentPhotos = [], initialPhotos = []) => {
  const photosWithNewMeta = []
  currentPhotos.forEach(({id, priority, main, destroy}) => {
    const diff = {}
    if (destroy) {
      diff.destroy = true
    } else {
      const initialPhoto = initialPhotos.filter(photo => photo.id === id)
      if (initialPhoto.length) {
        if (priority !== initialPhoto[0].priority) {
          diff.priority = priority
        }
        if (main !== initialPhoto[0].main) {
          diff.main = main
        }
      }
    }
    if (Object.keys(diff).length) {
      photosWithNewMeta.push({...diff, id})
    }
  })
  return photosWithNewMeta
}

const getPhotosWithNewFile = (currentPhotos = [], initialPhotos = []) => {
  const newMeta = []
  const newFiles = []
  currentPhotos.forEach(({id, name, priority, main, url}) => {
    const diff = {}
    const initialPhoto = initialPhotos.filter(photo => photo.id === id)
    if (initialPhoto.length) {
      diff.id = id
      if (priority !== initialPhoto[0].priority) {
        diff.priority = priority
      }
      if (main !== initialPhoto[0].main) {
        diff.main = main
      }
    }
    if (Object.keys(diff).length) {
      newMeta.push(diff)
      newFiles.push(dataURLtoFile(url, name))
    }
  })
  return {meta: newMeta, photos: newFiles}
}

export class Container extends React.Component {
  errors = config.updateProfileForm.errors

  addressField = ['country', 'region', 'city', 'location', 'street', 'zip', 'monfriFrom', 'monfriTo', 'saturdayFrom', 'saturdayTo', 'sundayFrom', 'sundayTo']
  phonesField = ['phone', 'visible', 'viber', 'whatsapp']
  successFullyText = 'Uspješno sačuvano!'
  state = {wasUpdated: false}

  validate = (values) => {
    const { i18n: { t } } = this.props

    const error = {}
    if (!requiredValidator(values.name)) {
      error.name = 'Name is not filled'
    }

    if (values.userAddresses && Array.isArray(values.userAddresses)) {
      const addressesError = []
      values.userAddresses.forEach((address, index) => {
        const errors = {}
        if (address) {
          if (!requiredValidator(address.country)) {
            errors.country = 'Country is not selected'
          }
          if (!requiredValidator(address.region)) {
            errors.region = 'Region is not selected'
          }
          if (!requiredValidator(address.city)) {
            errors.city = 'City is not selected'
          }
          if (!requiredValidator(address.location)) {
            errors.location = 'Location is not selected'
          }

          if (isBusinessUser(this.props.type)) {
            if (!requiredValidator(address.street)) {
              errors.street = 'Street is not set'
            }
          }
        } else {
          errors.country = 'Country is not selected'
          errors.region = 'Region is not selected'
          errors.city = 'City is not selected'
          errors.location = 'Location is not selected'
          if (isBusinessUser(this.props.type)) {
            errors.street = 'Street is not set'
          }
        }

        if (Object.keys(errors).length) {
          addressesError[index] = {...errors}
        }
      })
      if (addressesError.length) {
        error.userAddresses = [...addressesError]
      }
    }

    if (values.phones && Array.isArray(values.phones)) {
      const phonesErrors = []
      values.phones.forEach((phone, index) => {
        const errors = {}
        if (phone) {
          const number = phone.phone ? phone.phone.replace(/ /g, '').replace(/\+/g, '') : null
          if (!phoneValidator(number)) {
            errors.phone = 'Phone is incorrect'
          }
          if (!phone.id && !phone.code) {
            errors.code = 'Code is incorrect'
          }
        } else {
          errors.phone = 'Phone is incorrect'
          errors.code = 'Code is incorrect'
        }
        if (Object.keys(errors).length) {
          phonesErrors[index] = {...errors}
        }
      })
      if (phonesErrors.length) {
        error.phones = [...phonesErrors]
      }
    }

    if (!isBusinessUser(this.props.type)) {
      if (!requiredValidator(values.gender)) {
        error.gender = 'Gender is not set'
      }
    }

    if (values.email && !emailValidator(values.email)) {
      error.email = 'Email is incorrect'
    }

    if (values.password || values.confirmPassword) {
      const passwordCorrect = passwordValidator(values.password)
      const confirmCorrect = passwordValidator(values.confirmPassword)
      if (!passwordCorrect) {
        error.password = 'Password is incorrect'
      }
      if (!confirmCorrect) {
        error.confirmPassword = 'Password is incorrect'
      }
      if (passwordCorrect && confirmCorrect && values.password !== values.confirmPassword) {
        error.confirmPassword = 'Passwords do not match'
        error.password = 'Passwords do not match'
      }
    }

    if (this.props.photos.filter(photo => !photo.destroy).length > this.props.photoLimit) {
      error.photos = 'Too much photos was added'
    }

    if (Object.keys(error).length) {
      error._error = 'Registration failed'
      this.props.setProfileUpdateError(t('common:profileUpdateDefaultError'))
      throw new SubmissionError(error)
    }
  }

  prepareFields = (values) => {
    const diff = {}
    if (this.props.initialValues.name !== values.name) {
      diff.name = values.name === '' ? null : values.name
    }
    if (!isBusinessUser(this.props.type)) {
      if (this.props.initialValues.surname !== values.surname) {
        diff.surname = values.surname === '' ? null : values.surname
      }
      if (this.props.initialValues.gender !== values.gender) {
        diff.gender = values.gender
      }
      if (
        this.props.initialValues.year !== values.year ||
        this.props.initialValues.month !== values.month ||
        this.props.initialValues.day !== values.day
      ) {
        if (!values.year && !values.month && !values.day) {
          diff.birthday = null
        } else {
          const date = new Date(values.year, values.month, values.day)
          diff.birthday = `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
        }
      }
    } else {
      const logoDiff = getLogoDiff(this.props.existingLogo, this.props.logo)
      if (logoDiff) {
        diff.logo = logoDiff.logo
      }
      const bannerDiff = getBannerDiff(this.props.existingBanner, this.props.banner)
      if (bannerDiff) {
        diff.banner = bannerDiff.banner
      }
      if (isServiceProvider(this.props.type)) {
        if (this.props.initialValues.description !== values.description) {
          diff.description = values.description === '' ? null : values.description
        }
        if (Array.isArray(this.props.photos) && this.props.photos.length) {
          const preparedPhotosWithNewMeta = getPhotosWithNewMeta(
            this.props.photos.filter(photo => photo.id && photo.isOriginal),
            this.props.existingPhotos
          )
          if (preparedPhotosWithNewMeta.length) {
            diff.media = JSON.stringify(preparedPhotosWithNewMeta)
          }
          const mediaMeta = []
          const mediaNew = []
          const preparedPhotosWithNewFile = getPhotosWithNewFile(
            this.props.photos.filter(photo => photo.id && !photo.isOriginal),
            this.props.existingPhotos
          )
          if (
            preparedPhotosWithNewFile.photos.length &&
            preparedPhotosWithNewFile.meta.length &&
            preparedPhotosWithNewFile.meta.length === preparedPhotosWithNewFile.photos.length
          ) {
            mediaMeta.push(...preparedPhotosWithNewFile.meta)
            mediaNew.push(...preparedPhotosWithNewFile.photos)
          }
          const preparedNewPhotos = this.getNewPhotos(this.props.photos.filter(photo => !photo.id))
          if (
            preparedNewPhotos.photos.length &&
            preparedNewPhotos.meta.length &&
            preparedNewPhotos.meta.length === preparedNewPhotos.photos.length
          ) {
            mediaMeta.push(...preparedNewPhotos.meta)
            mediaNew.push(...preparedNewPhotos.photos)
          }
          if (mediaNew.length && mediaMeta.length && mediaMeta.length === mediaNew.length) {
            diff.mediaMeta = JSON.stringify(mediaMeta)
            diff.mediaNew = [...mediaNew]
          }
        }
      }
    }

    const addressesForUpdate = this.getAddressesForUpdate(values.userAddresses.filter(item => item.id))
    const deletedAddresses = this.getDeletedAddresses(values.userAddresses.filter(item => item.id).map(item => item.id))
    if (deletedAddresses.length) {
      addressesForUpdate.push(...deletedAddresses)
    }
    if (addressesForUpdate.length) {
      diff.addressesUpdate = JSON.stringify(addressesForUpdate)
    }
    const newAddresses = this.getNewAddresses(values.userAddresses.filter(item => !item.id))
    if (newAddresses.length) {
      diff.addressesAdd = JSON.stringify(newAddresses)
    }

    const phonesForUpdate = this.getPhonesForUpdate(values.phones.filter(item => item.id))
    if (phonesForUpdate.length) {
      diff.phonesUpdate = JSON.stringify(phonesForUpdate)
    }
    const newPhones = this.getNewPhones(values.phones.filter(item => !item.id))
    if (newPhones.length) {
      diff.phonesAdd = JSON.stringify(newPhones)
    }
    if (this.props.initialValues.email !== values.email) {
      diff.email = values.email === '' ? null : values.email
    }

    if (values.password) {
      diff.password = values.password
    }

    return diff
  }

  getAddressesForUpdate = addresses => {
    const addressesForUpdate = addresses.map(address => {
      const changes = {}
      const initValues = this.props.initialValues.userAddresses.filter(item => item.id === address.id)
      if (initValues.length) {
        this.addressField.forEach(field => {
          switch (field) {
            case 'country':
            case 'region':
            case 'city':
            case 'location':
              if (Number(address[field]) !== Number(initValues[0][field])) {
                changes[field] = address[field] === '' ? null : address[field]
              }
              break
            default:
              if (address[field] !== initValues[0][field]) {
                changes[field] = address[field]
              }
          }
        })
      }
      return Object.keys(changes).length ? {...changes, id: address.id} : null
    })
    return addressesForUpdate.filter(item => item)
  }

  getDeletedAddresses = addresses => {
    const initAddresses = this.props.initialValues.userAddresses.filter(item => item.id).map(item => item.id)
    return initAddresses.filter(value => addresses.indexOf(value) === -1).map(item => ({id: item, destroy: true}))
  }

  getNewAddresses = addresses => {
    const newAddresses = addresses.map(address => {
      const notEmptyValues = {}
      for (let field in address) {
        if (address.hasOwnProperty(field)) {
          if (address[field]) {
            notEmptyValues[field] = address[field]
          }
        }
      }
      return Object.keys(notEmptyValues).length ? notEmptyValues : null
    })
    return newAddresses.filter(item => item)
  }

  getPhonesForUpdate = phones => {
    const phonesForUpdate = phones.map(phone => {
      const changes = {}
      const initValues = this.props.initialValues.phones.filter(item => item.id === phone.id)
      if (initValues.length) {
        this.phonesField.forEach(field => {
          switch (field) {
            case 'phone':
              break
            default:
              if (phone[field] !== initValues[0][field]) {
                changes[field] = Boolean(phone[field])
              }
          }
        })
      }
      return Object.keys(changes).length ? {...changes, id: phone.id} : null
    })
    return phonesForUpdate.filter(item => item)
  }

  getNewPhones = phones => {
    const newPhones = phones.map(phone => {
      const values = {}
      for (let field in phone) {
        if (phone.hasOwnProperty(field)) {
          switch (field) {
            case 'phone':
              if (phone[field]) {
                values[field] = phone[field].replace(/ /g, '').replace(/\+/g, '')
              }
              break
            case 'viber':
            case 'whatsapp':
            case 'visible':
              values[field] = Boolean(phone[field])
              break
            default:
              if (phone[field]) {
                values[field] = phone[field]
              }
          }
        }
      }
      return Object.keys(values).length ? values : null
    })
    return newPhones.filter(item => item)
  }

  getNewPhotos = (photos = []) => {
    return {
      meta: photos.map(({main, priority}) => ({main, priority})),
      photos: photos.map(({url, name}) => dataURLtoFile(url, name))
    }
  }

  onSubmit = async values => {
    await this.setState({...this.state, wasUpdated: false})
    await this.props.setProfileUpdateError('')
    this.validate(values)
    const preparedFields = this.prepareFields(values)
    if (Object.keys(preparedFields).length) {
      const fieldsWithFiles = ['logo', 'mediaNew']
      const formData = new FormData()
      formData.append('userId', this.props.id)
      formData.append('user', this.props.id)

      const cookie = new CookiesHelper()
      formData.append('token', cookie.get('token'))

      for (let field in preparedFields) {
        if (preparedFields.hasOwnProperty(field)) {
          if (!fieldsWithFiles.includes(field) || preparedFields[field] === null) {
            formData.append(field, preparedFields[field])
          }
        }
      }
      if (preparedFields.logo) {
        formData.append('logo', preparedFields.logo)
      }
      if (Array.isArray(preparedFields.mediaNew) && preparedFields.mediaNew.length) {
        for (let key in preparedFields.mediaNew) {
          if (preparedFields.mediaNew.hasOwnProperty(key)) {
            formData.append('mediaNew', preparedFields.mediaNew[key])
          }
        }
      }
      for (let data of formData.entries()) console.log(data)
      await this.updateProfile(formData)
    }
  }

  onPhoneSubmit = async values => {
    const formData = new FormData()
    formData.append('userId', this.props.id)
    formData.append('user', this.props.id)
    const cookie = new CookiesHelper()
    formData.append('token', cookie.get('token'))
    formData.append('phonesAdd', JSON.stringify([values]))
    await this.updateProfile(formData)
  }

  updateProfile = async data => {
    const { i18n: { t } } = this.props

    const api = new ProfileApi()
    this.props.setProfileLoading(true)
    const updateResult = await api.updateProfile(data)
    try {
      if (updateResult.status === ProfileApi.responseTypes.OK) {
        switch (updateResult.data.updateStatus) {
          case ProfileApi.UpdateProfileStatusCodes.UPDATE_SUCCESSFULLY:
            setTimeout(() => {
              this.props.setUserInfo(updateResult.data.user)
            }, 3000)
            this.props.setProfileLoading(false)
            this.setState({...this.state, wasUpdated: true})

            if (updateResult.data.token && updateResult.data.user && updateResult.data.user.id) {
              const cookie = new CookiesHelper()
              cookie.set('token', updateResult.data.token, { maxAge: 315360000 })
              cookie.set('userId', updateResult.data.user.id, { maxAge: 315360000 })
              this.props.setIsAuthorized(true)
              this.props.setNotifications([])
            }

            window.scrollTo(0, 0)
            break
          case ProfileApi.UpdateProfileStatusCodes.PHONE_VERIFICATION_FAILED:
            this.props.setProfileUpdateError(t('common:profileUpdateSmsCodeError'))
            this.props.setProfileLoading(false)
            break
          case ProfileApi.UpdateProfileStatusCodes.PHONE_ALREADY_EXIST:
            this.props.setProfileUpdateError(t('common:profileUpdatePhoneError'))
            this.props.setProfileLoading(false)
            break
          case ProfileApi.UpdateProfileStatusCodes.EMAIL_IS_NOT_UNIQUE:
            this.props.setProfileUpdateError(t('common:profileUpdateEmailError'))
            this.props.setProfileLoading(false)
            break
          default:
            this.props.setProfileUpdateError(t('common:profileUpdateDefaultError'))
            this.props.setProfileLoading(false)
        }
      } else {
        this.props.setProfileUpdateError(updateResult?.error || t('common:profileUpdateDefaultError'))
        this.props.setProfileLoading(false)
      }
    } catch (e) {
      this.props.setProfileUpdateError(updateResult?.error || t('common:profileUpdateDefaultError'))
      this.props.setProfileLoading(false)
    }
  }

  hideSuccessfullyNotification = () => {
    this.setState({...this.state, wasUpdated: false})
  }

  render () {
    const { type, i18n: { t } } = this.props
    const isBusiness = isBusinessUser(type)
    const isSP = isServiceProvider(type)

    return (
      <form className='registracija-korisnik-form' action='' onSubmit={this.props.handleSubmit(this.onSubmit)}>
        <Title />
        {isSP ? <Description /> : null}
        {isBusiness ? <><Logo /> <Banner /></> : null}
        <Addresses />
        <Contacts onPhoneSubmit={this.onPhoneSubmit} />
        {
          !isBusiness ? <AdditionalInfo /> : null
        }
        {/* {isSP ? <Photos /> : null} */}
        <ChangePassword />
        <button className='form-submit-btn' type='submit' disabled={this.props.isFormPristine}>{t('common:Sačuvaj promjene')}</button>
        <FormNotification key='errorNotification' />
        <FormNotification
          key='successNotification'
          hideNotification={this.hideSuccessfullyNotification}
          message={t(`common:${this.successFullyText}`)}
          isActive={this.state.wasUpdated}
        />
      </form>
    )
  }
}

Container.displayName = 'UpdateForm'

const mapStateToProps = state => {
  const values = getUserInfo(state)

  const initialValues = {}
  let existingLogo = null
  let existingBanner = null
  let existingPhotos = []
  let id
  let photoLimit = 0
  let isFormPristine
  const type = values && values.type && values.type.seo
  const photos = getServiceProviderPhotos(state)
  if (values) {
    if (values.id) {
      id = values.id
    }
    if (values.name) {
      initialValues.name = values.name
    }
    if (values.surname) {
      initialValues.surname = values.surname
    }
    if (values.description) {
      initialValues.description = values.description
    }
    if (values.birthday) {
      const date = new Date(values.birthday)
      initialValues.year = date.getFullYear()
      initialValues.month = date.getMonth()
      initialValues.day = date.getDate()
    }
    if (values.gender) {
      initialValues.gender = values.gender
    }
    if (values.userAddresses && Array.isArray(values.userAddresses)) {
      initialValues.userAddresses = values.userAddresses.map(
        ({id, country, region, city, location, street, zip, monfriFrom, monfriTo, saturdayFrom, saturdayTo, sundayFrom, sundayTo, main}) =>
          ({
            country: country && country.id ? `${country.id}` : '',
            city: city && city.id ? `${city.id}` : '',
            region: region && region.id ? `${region.id}` : '',
            location: location && location.id ? `${location.id}` : '',
            id,
            street,
            zip,
            monfriFrom,
            monfriTo,
            saturdayFrom,
            saturdayTo,
            sundayFrom,
            sundayTo,
            main
          })
      )
    }
    if (values.phones && Array.isArray(values.phones)) {
      initialValues.phones = values.phones.map(({id, phone, viber, whatsapp, visible}) => ({
        id, phone: getFormattedPhoneNumber(phone), viber, whatsapp, visible
      }))
    }
    if (values.email && values.email.address) {
      initialValues.email = values.email.address
    }
    if (values.media && Array.isArray(values.media)) {
      const logo = values.media.find(item => item.title === 'profile-picture')
      if (logo) {
        existingLogo = logo.fd
      }
      const banner = values.media.find(item => item.title === 'banner-image')
      if (banner) {
        existingBanner = banner.fd
      }
      const photos = values.media.filter(item => item.title === 'gallery-image')
      if (photos.length) {
        existingPhotos = photos.map(({id, priority, main}) => ({id, priority, main}))
      }
    }
    if (values.packages && Array.isArray(values.packages) && values.packages.length) {
      photoLimit = Number(values.packages[0].photoLimit)
    }
  }
  const logo = getNewLogo(state)
  const banner = getNewBanner(state)
  if (isBusinessUser(type)) {
    if (isServiceProvider(type)) {
      const photosWithNewMetaDiff = getPhotosWithNewMeta(photos, existingPhotos)
      const isPhotosWithNewFileChanged = photos.filter(photo => photo.id && !photo.isOriginal).length
      isFormPristine = isPristine('update')(state) &&
        !getLogoDiff(existingLogo, logo) && !getBannerDiff(existingBanner, banner) &&
        !isPhotosWithNewFileChanged && !photosWithNewMetaDiff.length
    } else {
      isFormPristine = isPristine('update')(state) &&
        !getLogoDiff(existingLogo, logo) && !getBannerDiff(existingBanner, banner)
    }
  } else {
    isFormPristine = isPristine('update')(state)
  }
  return {
    id,
    initialValues,
    type,
    logo,
    existingLogo,
    banner,
    existingBanner,
    existingPhotos,
    photos,
    photoLimit,
    isFormPristine
  }
}

Container.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  setProfileUpdateError: PropTypes.func.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string.isRequired,
    surname: PropTypes.string,
    description: PropTypes.string,
    userAddresses: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired
    })).isRequired,
    phones: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired
    })),
    email: PropTypes.string,
    gender: PropTypes.oneOf(['f', 'm']),
    year: PropTypes.number,
    month: PropTypes.number,
    day: PropTypes.number
  }),
  logo: PropTypes.shape({
    name: PropTypes.string.isRequired,
    file: PropTypes.string.isRequired,
    isOriginal: PropTypes.bool
  }),
  existingLogo: PropTypes.string,
  banner: PropTypes.shape({
    name: PropTypes.string.isRequired,
    file: PropTypes.string.isRequired,
    isOriginal: PropTypes.bool
  }),
  existingBanner: PropTypes.string,
  id: PropTypes.number.isRequired,
  setUserInfo: PropTypes.func.isRequired,
  setProfileLoading: PropTypes.func.isRequired,
  photos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    priority: PropTypes.number,
    main: PropTypes.bool,
    url: PropTypes.string,
    isOriginal: PropTypes.bool,
    destroy: PropTypes.bool
  })),
  existingPhotos: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    priority: PropTypes.number.isRequired,
    main: PropTypes.bool.isRequired
  })),
  photoLimit: PropTypes.number.isRequired,
  setIsAuthorized: PropTypes.func.isRequired,
  setNotifications: PropTypes.func.isRequired,
  isFormPristine: PropTypes.bool.isRequired
}

Container.defaultProps = {
  type: '',
  photos: [],
  photoLimit: 0,
  isFormPristine: true
}

const enhance = compose(
  connect(
    mapStateToProps,
    {setProfileUpdateError, setUserInfo, setProfileLoading, setIsAuthorized, setNotifications}
  ),
  withTranslation
)

export default enhance(
  reduxForm({
    form: 'update',
    destroyOnUnmount: false,
    enableReinitialize: true
  })(Container)
)
